import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Check, Users, RefreshCw } from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTheme } from './ThemeToggle';

export default function PollsWidget({ customerId }) {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votedPolls, setVotedPolls] = useState([]);
  const { isDarkMode } = useTheme();

  const loadPolls = async () => {
    try {
      const data = await api.getActivePolls();
      setPolls(data.polls || []);
      
      if (customerId) {
        const votes = await api.getCustomerVotes(customerId);
        setVotedPolls(votes.votedPollIds || []);
      }
    } catch (error) {
      console.error('Erreur chargement sondages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolls();
  }, [customerId]);

  const handleVote = async (pollId, optionIndex) => {
    if (!customerId) {
      toast.error('Connectez-vous pour voter');
      return;
    }
    
    if (votedPolls.includes(pollId)) {
      toast.info('Vous avez déjà voté');
      return;
    }

    try {
      await api.submitVote(pollId, optionIndex, customerId);
      toast.success('Vote enregistré ! 🗳️');
      setVotedPolls([...votedPolls, pollId]);
      loadPolls();
    } catch (error) {
      toast.error('📊 Vote non enregistré. Réessayez !');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <motion.div 
          className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <BarChart3 className="w-5 h-5 text-violet-400" />
        </motion.div>
      </div>
    );
  }

  // État vide stylisé
  if (polls.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #2e1065 100%)',
          boxShadow: '0 0 40px rgba(139, 92, 246, 0.15), 0 15px 30px rgba(0,0,0,0.4)',
          border: '1px solid rgba(139, 92, 246, 0.2)'
        }}
      >
        {/* Étoiles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-violet-400"
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
              animate={{ opacity: [0.1, 0.4, 0.1] }}
              transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
            />
          ))}
        </div>

        {/* HUD Corners */}
        <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-violet-500/40" />
        <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-violet-500/40" />
        <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-violet-500/20" />
        <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-violet-500/20" />

        <div className="relative px-5 py-8 text-center">
          <motion.div
            className="w-14 h-14 mx-auto mb-3 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center"
            animate={{ scale: [1, 1.05, 1], boxShadow: ['0 0 10px rgba(139, 92, 246, 0.1)', '0 0 20px rgba(139, 92, 246, 0.3)', '0 0 10px rgba(139, 92, 246, 0.1)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <BarChart3 className="w-7 h-7 text-violet-400/50" />
          </motion.div>
          <h4 className="text-white font-bold mb-1">💭 Sondages</h4>
          <p className="text-gray-400 text-sm">Aucune question pour le moment</p>
          <p className="text-xs text-gray-600 mt-1 font-mono">STANDBY MODE • Reviens bientôt ! 😊</p>
        </div>

        {/* Footer HUD */}
        <div className="px-4 py-2 border-t border-violet-500/10 flex items-center justify-between">
          <span className="text-[10px] font-mono text-gray-600">💭 POLLS v2.0</span>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-500/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-violet-400/20" />
            <div className="w-1.5 h-1.5 rounded-full bg-violet-300/10" />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #2e1065 100%)',
        boxShadow: '0 0 40px rgba(139, 92, 246, 0.2), 0 15px 30px rgba(0,0,0,0.4)',
        border: '1px solid rgba(139, 92, 246, 0.3)'
      }}
    >
      {/* Étoiles animées */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-violet-400"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
            animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />
      
      {/* HUD Corners */}
      <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-violet-500/50" />
      <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-violet-500/50" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-violet-500/30" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-violet-500/30" />

      {/* Header */}
      <div className="relative p-4 flex items-center justify-between border-b border-violet-500/20">
        <div className="flex items-center gap-3">
          <motion.div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.3)'
            }}
            animate={{ boxShadow: ['0 0 10px rgba(139, 92, 246, 0.2)', '0 0 20px rgba(139, 92, 246, 0.4)', '0 0 10px rgba(139, 92, 246, 0.2)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="text-xl">💭</span>
          </motion.div>
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              On t'écoute !
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-violet-500/20 text-violet-400 border border-violet-500/30">
                {polls.length} ACTIF{polls.length > 1 ? 'S' : ''}
              </span>
            </h3>
            <p className="text-[11px] text-violet-500/70 font-mono tracking-wider">
              TON AVIS COMPTE • VOTE ANONYME
            </p>
          </div>
        </div>
        
        <Button
          onClick={loadPolls}
          size="sm"
          variant="ghost"
          className="text-gray-400 hover:text-white hover:bg-violet-500/10"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Sondages */}
      <div className="relative p-4 space-y-4">
        {polls.map((poll) => (
          <PollCard
            key={poll.id}
            poll={poll}
            hasVoted={votedPolls.includes(poll.id)}
            onVote={(optionIndex) => handleVote(poll.id, optionIndex)}
          />
        ))}
      </div>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-violet-500/10 flex items-center justify-between">
        <span className="text-[10px] font-mono text-gray-600">💭 POLLS v2.0</span>
        <div className="flex items-center gap-1">
          <motion.div className="w-1.5 h-1.5 rounded-full bg-violet-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
          <div className="w-1.5 h-1.5 rounded-full bg-violet-400/50" />
          <div className="w-1.5 h-1.5 rounded-full bg-violet-300/30" />
        </div>
      </div>
    </motion.div>
  );
}

function PollCard({ poll, hasVoted, onVote }) {
  const totalVotes = poll.options.reduce((acc, opt) => acc + (opt.votes || 0), 0);

  return (
    <div 
      className="rounded-xl overflow-hidden"
      style={{
        background: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(139, 92, 246, 0.2)'
      }}
    >
      <div className="p-4">
        {/* Question */}
        <div className="flex items-start gap-3 mb-4">
          <span className="text-2xl">{poll.emoji || '🤔'}</span>
          <div>
            <h4 className="font-bold text-white">{poll.question}</h4>
            <div className="flex items-center gap-2 mt-1">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-500">{totalVotes} vote(s)</span>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-2">
          {poll.options.map((option, idx) => {
            const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
            const isWinning = hasVoted && option.votes === Math.max(...poll.options.map(o => o.votes));

            return (
              <motion.button
                key={idx}
                onClick={() => !hasVoted && onVote(idx)}
                disabled={hasVoted}
                className={`w-full relative overflow-hidden rounded-xl p-3 text-left transition-all ${
                  hasVoted ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02]'
                } ${isWinning ? 'ring-2 ring-green-400' : ''}`}
                style={{
                  background: hasVoted 
                    ? `linear-gradient(90deg, rgba(139, 92, 246, 0.3) ${percentage}%, rgba(0,0,0,0.3) ${percentage}%)`
                    : 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(139, 92, 246, 0.2)'
                }}
                whileHover={!hasVoted ? { scale: 1.02 } : {}}
                whileTap={!hasVoted ? { scale: 0.98 } : {}}
              >
                <div className="flex items-center justify-between relative z-10">
                  <span className="font-medium text-white flex items-center gap-2">
                    {option.text}
                    {isWinning && <span className="text-green-400">👑</span>}
                  </span>
                  {hasVoted ? (
                    <span className="text-violet-400 font-bold">{percentage}%</span>
                  ) : (
                    <span className="text-gray-500 text-sm">Cliquez pour voter</span>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Statut */}
        {hasVoted && (
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
            <Check className="w-4 h-4 text-green-400" />
            <span className="text-green-400 text-sm">Merci pour votre vote !</span>
          </div>
        )}
      </div>
    </div>
  );
}
